import { useEffect, useCallback } from 'react';
import CastButton from './CastButton';
import PartyCanvas from './PartyCanvas';
import PartyOverlay from './PartyOverlay';
import { useSpotifyPartySync } from './hooks/useSpotifyPartySync';
import { useSpotifyMicPartySync } from './hooks/useSpotifyMicPartySync';
import { usePartyDemoSync } from './hooks/usePartyDemoSync';
import { primeMicAudioContext, useMicPartySync } from './hooks/useMicPartySync';
import { useKeyboardControls } from './hooks/useKeyboardControls';
import { usePartyStore } from './store/partyStore';
import { SCENE_IDS, type SceneId } from './types';

interface PartyModeProps {
  clientId: string;
  spotifyConnected: boolean;
  spotifyUsername: string;
  onConnect: () => void;
  onDisconnect: () => void;
  authLoading: boolean;
  authError: string;
  redirectUri: string;
  hasDefaultClientId: boolean;
  clientIdInput: string;
  onClientIdChange: (id: string) => void;
  demoMode?: boolean;
}

function SpotifyIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
    </svg>
  );
}

export default function PartyMode({
  clientId,
  spotifyConnected,
  spotifyUsername,
  onConnect,
  onDisconnect,
  authLoading,
  authError,
  redirectUri,
  hasDefaultClientId,
  clientIdInput,
  onClientIdChange,
  demoMode = false,
}: PartyModeProps) {
  const running = usePartyStore((s) => s.running);
  const inputMode = usePartyStore((s) => s.inputMode);
  const setRunning = usePartyStore((s) => s.setRunning);
  const setInputMode = usePartyStore((s) => s.setInputMode);
  const setClientId = usePartyStore((s) => s.setClientId);
  const setSpotifyConnected = usePartyStore((s) => s.setSpotifyConnected);
  const setLyricsEnabled = usePartyStore((s) => s.setLyricsEnabled);
  const setForcedScene = usePartyStore((s) => s.setForcedScene);
  const setSceneLock = usePartyStore((s) => s.setSceneLock);
  const displayLyric = usePartyStore((s) => s.displayLyric);
  const overrides = usePartyStore((s) => s.overrides);

  useEffect(() => {
    setClientId(clientId);
    setSpotifyConnected(spotifyConnected, spotifyUsername);
  }, [clientId, spotifyConnected, spotifyUsername, setClientId, setSpotifyConnected]);

  useSpotifyPartySync(clientId, running && inputMode === 'spotify' && spotifyConnected && !demoMode);
  const { error: spotifyMicError } = useSpotifyMicPartySync(clientId, running && inputMode === 'spotify-mic' && spotifyConnected && !demoMode);
  usePartyDemoSync(running && inputMode === 'demo');
  const { error: micError } = useMicPartySync(running && inputMode === 'mic');
  useKeyboardControls(running);

  useEffect(() => {
    if (!demoMode) return;
    const params = new URLSearchParams(window.location.search);
    const requestedScene = params.get('scene') as SceneId | null;
    const forcedScene = requestedScene && SCENE_IDS.includes(requestedScene)
      ? requestedScene
      : null;
    const lyricsParam = params.get('lyrics');
    const lyricsEnabled = lyricsParam === '1' || lyricsParam === 'true';

    setInputMode('demo');
    setLyricsEnabled(lyricsEnabled);
    setForcedScene(forcedScene);
    setSceneLock(Boolean(forcedScene));
    setRunning(true);
    return () => {
      setSceneLock(false);
      setForcedScene(null);
      setRunning(false);
    };
  }, [demoMode, setForcedScene, setInputMode, setLyricsEnabled, setRunning, setSceneLock]);

  useEffect(() => {
    if (!running || !('wakeLock' in navigator)) return;
    let lock: { release: () => Promise<void> } | null = null;
    navigator.wakeLock.request('screen').then((wl) => { lock = wl; }).catch(() => {});
    return () => { lock?.release().catch(() => {}); };
  }, [running]);

  const handleStart = () => {
    if (!spotifyConnected) return;
    setInputMode('spotify');
    setRunning(true);
  };

  const handlePreview = () => {
    setInputMode('demo');
    setRunning(true);
  };

  const handleMicStart = () => {
    setInputMode('mic');
    primeMicAudioContext().catch(() => {});
    setRunning(true);
  };

  const handleSpotifyMicStart = () => {
    if (!spotifyConnected) return;
    setInputMode('spotify-mic');
    primeMicAudioContext().catch(() => {});
    setRunning(true);
  };

  const handleStop = () => {
    setInputMode(spotifyConnected ? 'spotify' : 'demo');
    setRunning(false);
  };

  const handleFullscreen = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen();
    else document.documentElement.requestFullscreen().catch(() => {});
  }, []);

  if (!running) {
    return (
      <div className="party-landing">
        <div className="party-landing-inner">
          <div className="party-title-group">
            <span className="party-title-main">PARTY</span>
            <span className="party-title-sub">LIVE EDM VISUALIZER</span>
          </div>

          <p className="party-landing-desc">
            Festival-style visuals synced to Spotify playback.
            Deterministic, lyric-aware, queue-preprocessed — no AI at runtime.
          </p>

          <div className="party-spotify-panel">
            {authLoading ? (
              <p className="party-muted">Connecting to Spotify…</p>
            ) : spotifyConnected ? (
              <div className="party-connected-box">
                <div className="party-connected-badge">
                  <SpotifyIcon />
                  Connected{spotifyUsername ? ` as ${spotifyUsername}` : ''}
                </div>
                <p className="party-muted">Play music in Spotify, then start the visualizer.</p>
                <button type="button" className="party-disconnect" onClick={onDisconnect}>Disconnect</button>
              </div>
            ) : (
              <div className="party-setup">
                <p className="party-setup-title">Connect Spotify</p>
                {hasDefaultClientId ? (
                  <p className="party-muted">Sign in with Spotify to sync playback from your device.</p>
                ) : (
                  <>
                    <p className="party-muted">Add this Redirect URI in your Spotify app settings:</p>
                    <code className="party-uri">{redirectUri}</code>
                    <input
                      className="party-input"
                      type="text"
                      placeholder="Spotify Client ID…"
                      value={clientIdInput}
                      onChange={(e) => onClientIdChange(e.target.value)}
                      spellCheck={false}
                      autoComplete="off"
                    />
                  </>
                )}
                {authError && <p className="party-error">{authError}</p>}
                <button type="button" className="party-connect-btn" onClick={onConnect}>
                  <SpotifyIcon /> Connect Spotify
                </button>
              </div>
            )}
          </div>

          <div className="party-input-mode-panel">
            <p className="party-setup-title">Live Input</p>
            <p className="party-muted">Use your microphone for local audio-reactive visuals.</p>
            {(micError || spotifyMicError) && <p className="party-error">{micError || spotifyMicError}</p>}
            <div className="party-live-input-actions">
              {spotifyConnected && (
                <button type="button" className="party-spotify-mic-btn" onClick={handleSpotifyMicStart}>
                  Spotify + Mic
                </button>
              )}
              <button type="button" className="party-mic-btn" onClick={handleMicStart}>
                Mic Mode
              </button>
            </div>
          </div>

          {spotifyConnected && (
            <button type="button" className="party-start-btn" onClick={handleStart}>
              Start Visualizer
            </button>
          )}

          {!spotifyConnected && (
            <div className="party-demo-actions">
              <button type="button" className="party-preview-btn" onClick={handlePreview}>
                Preview Visuals
              </button>
              <a className="party-demo-link" href="/party-demo">
                Demo Link
              </a>
            </div>
          )}

          <CastButton variant="landing" />

          <p className="party-footnote">
            Spotify + Mic uses Spotify for lyrics and album art while the browser listens locally for the visual pulse.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="party-root">
      <PartyCanvas />
      {displayLyric && overrides.lyricsEnabled && !overrides.blackout && (
        <div className="party-hero-lyric">{displayLyric}</div>
      )}
      <PartyOverlay onStop={handleStop} onFullscreen={handleFullscreen} />
    </div>
  );
}
